import { FocusEventHandler, forwardRef } from 'react';
import { NumericFormatProps, OnValueChange } from 'react-number-format';

import {
  DefaultComponentProps,
  ElementSize,
  Nullable,
  OmitStrict,
  RenderPropFC,
} from '../../../../types';
import { cn, renderFCRenderProp } from '../../../../utils';
import InputMainWrapper from '../../../Input/components/InputMainWrapper';
import { NumericInputClassNames } from '../../constants';
import useNumericInputValue from '../../hooks/useNumericInputValue';
import { NumericInputChangeHandler } from '../../types';

import getPreviewValue from './getPreviewValue';
import {
  NumberInputMask,
  NumericInputElement,
  NumericInputElementWrapper,
} from './NumericInputBox.styled';

type InputProps = OmitStrict<NumericFormatProps, 'value' | 'onChange'> & {
  onChange?: NumericInputChangeHandler;
  previewDecimalScale?: number;
  value?: Nullable<number | string>;
};

export type NumericInputBoxProps = React.HTMLAttributes<HTMLDivElement> &
  DefaultComponentProps & {
    elementSize: ElementSize;
    hasError: boolean | undefined;
    leading?: RenderPropFC;
    unit?: string;
    trailing?: RenderPropFC;
    inputProps?: InputProps;
  };

const NumericInputBox = forwardRef<HTMLDivElement, NumericInputBoxProps>(
  (props, rootRef) => {
    const {
      className,
      'data-testid': dataTestId,
      elementSize,
      hasError = false,
      leading,
      trailing,
      inputProps,
      unit,
      ...rest
    } = props;

    const {
      disabled,
      value: passedValue,
      decimalScale,
      previewDecimalScale,
      onFocus,
      onBlur,
      onChange,
      onValueChange,
      thousandSeparator = ',',
      className: inputClassName,
      ...restInputProps
    } = inputProps || {};

    const [inputValue, setInputValue] = useNumericInputValue(passedValue);

    const handleValueChange: OnValueChange = (formatValues, sourceInfo) => {
      setInputValue(formatValues.value);
      onValueChange?.(formatValues, sourceInfo);
      onChange?.(formatValues.floatValue);
    };

    const handleInputBlur: FocusEventHandler<HTMLInputElement> = event => {
      if (passedValue !== undefined) {
        setInputValue(passedValue);
      }
      onBlur?.(event);
    };

    const handleInputFocus: FocusEventHandler<HTMLInputElement> = event => {
      // sets cursor to the end of the input on focus
      event.target.setSelectionRange(
        event.target.value.length,
        event.target.value.length,
      );

      onFocus?.(event);
    };

    const decimals =
      previewDecimalScale !== undefined ? previewDecimalScale : decimalScale;

    const previewValue = getPreviewValue({
      inputValue,
      decimals,
    });

    return (
      <InputMainWrapper
        className={cn(NumericInputClassNames.MainWrapper, className)}
        data-testid={dataTestId}
        disabled={!!disabled}
        elementSize={elementSize}
        hasError={hasError}
        leading={leading}
        ref={rootRef}
        trailing={
          <>
            {unit}
            {renderFCRenderProp(trailing, {})}
          </>
        }
        {...rest}>
        <NumericInputElementWrapper
          $size={elementSize}
          className={NumericInputClassNames.MaskWrapper}>
          <NumericInputElement
            allowLeadingZeros
            $size={elementSize}
            allowNegative={false}
            className={cn(NumericInputClassNames.Main, inputClassName)}
            type="text"
            {...restInputProps}
            decimalScale={decimalScale}
            disabled={disabled}
            thousandSeparator={thousandSeparator}
            value={inputValue}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onValueChange={handleValueChange}
          />

          {!!inputValue && (
            <NumberInputMask
              $isDisabled={!!disabled}
              $size={elementSize}
              className={NumericInputClassNames.Mask}
              role="presentation">
              {previewValue}
            </NumberInputMask>
          )}
        </NumericInputElementWrapper>
      </InputMainWrapper>
    );
  },
);

NumericInputBox.displayName = 'NumericInputBox';

export default NumericInputBox;
